import type { ConnectSettingsTopic } from "@blpde/connect";

/**
 * The pages of Connect's own settings, in their order. `SettingsView` renders one
 * of them. The hub lists them in three entries of their own: „Allgemein" (the
 * account's overview, the Design and the dictionary of the spell check and the search — every
 * app's settings, listed before the apps), „System" (the platform settings
 * every user has) and „Development" (the developer tools).
 * Connect's scalar settings — Darstellung, Formulare, Tabellen, Shell, Suche —
 * are no pages: they are sections of the one settings list
 * (`connectSettingsSections.ts`, I-2510), which the hub lists next to these.
 */
export const CONNECT_SETTINGS_TAB_IDS = [
  "general",
  "design",
  "personalDictionary",
  "devices",
  "accountProfiles",
  "modulePasswords",
  "accountSharing",
  "search",
  "antivirus",
  "loginPage",
  "support",
  "mcp",
  "develop",
  "registry",
  "layouts",
] as const;

export type ConnectSettingsTabId = (typeof CONNECT_SETTINGS_TAB_IDS)[number];

export function isConnectSettingsTabId(
  value: unknown,
): value is ConnectSettingsTabId {
  return (
    typeof value === "string" &&
    (CONNECT_SETTINGS_TAB_IDS as readonly string[]).includes(value)
  );
}

/** The hub entries Connect's own settings are listed under; also their first URL segment. */
export type ShellSettingsModuleId = "general" | "system" | "development";

const SHELL_SETTINGS_MODULE: Record<
  ConnectSettingsTabId,
  ShellSettingsModuleId
> = {
  general: "general",
  design: "general",
  personalDictionary: "general",
  devices: "system",
  accountProfiles: "system",
  modulePasswords: "system",
  accountSharing: "system",
  search: "system",
  antivirus: "system",
  loginPage: "system",
  support: "system",
  mcp: "system",
  develop: "development",
  registry: "development",
  layouts: "development",
};

/** The hub entry a page of Connect's own settings belongs to. */
export function shellSettingsModuleOf(
  tab: ConnectSettingsTabId,
): ShellSettingsModuleId {
  return SHELL_SETTINGS_MODULE[tab];
}

/** Hidden search terms of each page — the names of its controls. */
const CONNECT_SETTINGS_KEYWORDS: Record<
  ConnectSettingsTabId,
  readonly string[]
> = {
  accountSharing: ["Konto", "Teilen", "Mail", "Kalender", "Account", "Sharing"],
  general: ["Benutzer", "Server", "Firma", "Mandanten-Container", "Konto"],
  devices: ["Geräte", "Sitzungen", "Abmelden", "Browser", "Desktop", "Devices"],
  accountProfiles: [
    "Profil",
    "Kontoprofil",
    "Standardprofil",
    "Büro",
    "Homeoffice",
    "Sitzung",
    "Profiles",
  ],
  modulePasswords: [
    "Modulpasswort",
    "Passwort",
    "Kontopasswort",
    "Mail",
    "IMAP",
    "SMTP",
    "Kalender",
    "CalDAV",
    "CardDAV",
    "openHAB",
    "Sites",
  ],
  search: [
    "Suche",
    "Suchquellen",
    "Befehlspalette",
    "Module",
    "Reihenfolge",
    "Priorität",
    "Arten",
    "Stichwörter",
    "Volltext",
    "Search",
  ],
  design: [
    "Design",
    "Designs",
    "Look-Designer",
    "Erscheinungsbild",
    "Farben",
    "Schrift",
    "Typografie",
    "Abstände",
  ],
  antivirus: [
    "Virenscanner",
    "Viren",
    "Whitelist",
    "Blacklist",
    "Sperrliste",
    "Hash",
    "SHA-256",
    "Fehlalarm",
    "Antivirus",
    "Virus",
    "Allow list",
    "Block list",
  ],
  loginPage: [
    "Anmeldeseite",
    "Login",
    "Anmeldung",
    "Branding",
    "Logo",
    "Hintergrund",
    "Domain",
    "Hostname",
    "Adresse",
    "Vorschau",
    "Login page",
  ],
  support: [
    "Fernwartung",
    "Support",
    "BLP",
    "Aufschalten",
    "Wartung",
    "Dauerfernwartung",
    "Zugriffe",
    "Superadmin",
    "Remote support",
  ],
  personalDictionary: [
    "Wörterbuch",
    "Benutzerwörterbuch",
    "Rechtschreibung",
    "Rechtschreibprüfung",
    "Wörter",
    "Dictionary",
    "Spelling",
    "Spell check",
    "Suchwörterbuch",
    "Synonyme",
    "Korrekturen",
    "Nie vorschlagen",
    "Search dictionary",
    "Synonyms",
    "Corrections",
    "Never suggest",
  ],
  mcp: [
    "Model Context Protocol",
    "Agent",
    "KI",
    "Endpunkt",
    "Werkzeuge",
    "Tools",
  ],
  develop: ["Entwickler", "Transport", "WebSocket", "Debug", "API", "Sitzung"],
  registry: ["Komponenten", "Components", "Registry"],
  layouts: ["Layouts", "Backing Layouts", "Masken"],
};

function topic(id: ConnectSettingsTabId): ConnectSettingsTopic {
  return {
    id,
    labelKey: `connect.settings.tabs.${id}`,
    keywords: CONNECT_SETTINGS_KEYWORDS[id],
    // The look designer („Design“) brings its own workspace (preview, inspector).
    workspace: id === "design",
    // The login page editor is a page of its own (logo, background, addresses
    // and a preview — not scalar rows), and so are remote support (the
    // account picker and the recent access) and the virus scan's lists; the
    // rest is SettingsView's.
    panel:
      id === "loginPage"
        ? () => import("@/settingsHub/LoginPageSettings.vue")
        : id === "support"
          ? () => import("@/settingsHub/SupportSettings.vue")
          : id === "antivirus"
            ? () => import("@/settingsHub/AntivirusListsSettings.vue")
            : () => import("@/views/SettingsView.vue"),
  };
}

/** The pages of the hub's „Allgemein" entry. */
export const generalSettingsTopics: ConnectSettingsTopic[] =
  CONNECT_SETTINGS_TAB_IDS.filter(
    (id) => SHELL_SETTINGS_MODULE[id] === "general",
  ).map(topic);

/** The pages of the hub's „System" entry. */
export const systemSettingsTopics: ConnectSettingsTopic[] =
  CONNECT_SETTINGS_TAB_IDS.filter(
    (id) => SHELL_SETTINGS_MODULE[id] === "system",
  ).map(topic);

/** The pages of the hub's „Development" entry. */
export const developmentSettingsTopics: ConnectSettingsTopic[] =
  CONNECT_SETTINGS_TAB_IDS.filter(
    (id) => SHELL_SETTINGS_MODULE[id] === "development",
  ).map(topic);
